
import {
  CFormField,
  setFormFieldsAbleBodied,
  setFormFieldsDisabled,
  TInput,
  validateFormFields,
} from "@/models/form_cards";
import { Component, Vue, Watch } from "vue-property-decorator";

import { HttpStatusCodes } from "@/constants/httpStatusCodes";
import { PROD } from "@/constants/url";
import router from "@/router";
import { SMApp, SMNotifications, SMUser } from "@/store";
import { isReturnableAppPath } from "@/utils/authRoutes";

type TLoginReqData = {
  email: string;
  password: string;
};

type TAccountActivationReqData = {
  token: string;
};

const CASES_ROUTE = "/cases";

@Component({
  components: {
    FormCard: () => import("@/components/FormCard/FormCard.vue"),
  },
})
export default class Login extends Vue {
  $refs!: {
    email: TInput;
    password: TInput;
  };

  get isProdEnv() {
    return process.env.NODE_ENV === PROD;
  }

  get inputs() {
    const { email, password } = this.$refs;

    return [email, password];
  }

  showEachInputError() {
    this.inputs.forEach((input) => input?.showError());
  }

  state = {
    isLoading: false,
    isAccountActivated: false,
    isAccountActivationFailed: false,
    isAccountActivationNeeded: false,
    isAnyCredentialWrong: false,
  };

  formTouched = false;

  @Watch("state.isLoading")
  isLoadingChanged(val) {
    if (!val) setFormFieldsAbleBodied(this.fields);

    if (!this.state.isAnyCredentialWrong) setFormFieldsDisabled(this.fields);

    this.showEachInputError();
  }

  @Watch("state.isAccountActivated")
  isAccountActivatedChanged() {
    if (!this.state.isAccountActivated) setFormFieldsAbleBodied(this.fields);
  }

  @Watch("state.isAccountActivationNeeded")
  isAccountActivationNeededChanges() {
    if (this.state.isAccountActivationNeeded) this.showEachInputError();
  }

  @Watch("state.isAnyCredentialWrong")
  isAnyCredentialWrongChanged(val) {
    if (val) this.showEachInputError();
  }

  email = new CFormField("Email")
    .addRuleByAlias("email")
    .addRule(() => !this.state.isAnyCredentialWrong || "");

  password = new CFormField("Password")
    .addRule(
      () => !this.state.isAnyCredentialWrong || "Oops! Either your email or password is wrong"
    )
    .addRule(() => !this.state.isAccountActivationNeeded || "Account is not activated");

  get fields() {
    const { email, password } = this;

    return [email, password];
  }

  get isFormValid() {
    const isEachFieldValid = validateFormFields(this.fields);

    return isEachFieldValid || this.formTouched;
  }

  getLoginData(): TLoginReqData {
    const { email, password } = this;

    return {
      email: email.value.trim().toLowerCase(),
      password: password.value,
    };
  }

  handleLoginError(code?: number) {
    let isEachInputErrorNeeded = true;

    if (code === HttpStatusCodes.UNAUTHORIZED) {
      this.state.isAnyCredentialWrong = true;
    } else if (code === HttpStatusCodes.FORBIDDEN) {
      this.state.isAccountActivationNeeded = true;
    } else {
      isEachInputErrorNeeded = false;
      console.warn("unexpected error status code:", code);
    }

    if (isEachInputErrorNeeded) this.showEachInputError();
  }

  get user() {
    return SMUser.user;
  }

  async login() {
    try {
      this.state.isLoading = true;

      const data = this.getLoginData();

      await this.$auth.login({ data });

      await SMUser.updateCurrentUser();

      await this.$nextTick();

      await this.navigateToAppPath();

      await SMNotifications.startNotificationsRefresh();
    } catch (error) {
      this.handleLoginError(error?.response?.status);
    } finally {
      this.state.isLoading = false;
    }
  }

  getDefaultAppPath(): string {
    if (SMUser.isCandidate) return CASES_ROUTE;

    const basePath = SMUser.basePath;

    if (!basePath) return "/auth/login";

    if (SMUser.isEmployer || SMUser.isEmployee) return `${basePath}/home`;

    return basePath;
  }

  async navigateToAppPath() {
    SMApp.refresh();

    const userID = String(SMUser.user?.ID);

    if (!SMUser.isValidationPassed) {
      localStorage.setItem("show_hello_msg_home_for_first_time_signed", userID);
      localStorage.setItem("show_hello_msg_services_for_first_time_signed", userID);
    }

    let path = this.getDefaultAppPath();

    const canUsePreviousPath =
      SMApp.previousPath &&
      isReturnableAppPath(SMApp.previousPath) &&
      ((SMApp.previousUserId && SMApp.previousUserId === SMUser.user?.ID) ||
        !SMApp.previousUserId ||
        SMApp.previousUserId === 0);

    if (canUsePreviousPath) {
      path = SMApp.previousPath;
    }

    SMApp.unsetPreviousPath();
    SMApp.unsetPreviousUserId();
    SMApp.checkAuthOnServer();

    await router.push(path);
  }

  redirectLogin() {
    const newUrl = window.location.href.split("?")[0];
    window.history.replaceState({}, document.title, newUrl);

    SMApp.unsetPreviousPath();
    SMApp.unsetPreviousUserId();

    this.state.isAccountActivated = false;
  }

  get routeQuery() {
    return this.$route.query;
  }

  @Watch("routeQuery", { immediate: true, deep: true })
  routeQueryChanged(query: any) {
    if (!query) return;

    this.checkAccountActivationToken(query);
  }

  activationErrorMessage = "";

  checkAccountActivationToken({ status }: { status: string }) {
    if (!status) return;

    if (status == "200") return this.showAccountActivationSuccessCard();

    this.activationErrorMessage = status;

    this.showAccountActivationFailureCard();
  }

  showAccountActivationSuccessCard() {
    this.state.isAccountActivated = true;
  }

  showAccountActivationFailureCard() {
    this.state.isAccountActivationFailed = true;
  }
}
